// A link that names something the ring does not hold (SPEC-viewfinder §6,
// ruling 1; #225).
//
// #225's promise is that applying a `sel` from the address puts that row in
// view. The list is the radius now, so such a `sel` can name something with no
// row at all — and an empty list with a detail nowhere on it is exactly the
// defect #225 exists to remove. The owner ruled: widen the ring to the smallest
// 10 m step that includes it, and past the 5 km ceiling `r` carries, pan the
// centre there instead. Then the row exists, and `axis-view.ts` scrolls to it
// under #225's own rule.
//
// A `sel` chosen by tapping can never be outside the ring — only rows inside it
// exist to tap — so this answers the address and nothing else.
import { derived } from '../derived.ts';
import { haversine } from '../shared/geo.ts';
import { moveCentre } from '../state/commands.ts';
import { radiusIncluding, viewState } from '../state/viewstate.ts';

/**
 * The selection this has already answered. Once per value, so that widening —
 * which changes the view, which derives again — cannot answer itself a second
 * time, and so that a resident who narrows the radius back down over an open
 * row is not dragged outwards again.
 *
 * Cleared when nothing is open, so the same link opened twice still works.
 */
let answered: string | null = null;

export function mountReach(): void {
  derived.subscribe(({ derived: axis, view, centre }) => {
    if (view.selected == null) {
      answered = null;
      return;
    }
    if (!axis || view.selected === answered) return;

    // The four feeds answer one at a time, so „this id is in no set" means „not
    // yet" far more often than „never": answering on the first derivation that
    // carried a row at all marked the link answered while the water fault it
    // named was still three pushes away (measured on the fixtures). The
    // selection counts as answered once the derivation KNOWS the item —
    // anywhere in the ring, outside it, or city-wide — and until then this
    // waits. An id nothing ever carries simply never answers, which costs three
    // array scans per push and holds nothing open.
    const out = axis.beyond.find((i) => i.id === view.selected);
    const known = axis.placed.some((i) => i.id === view.selected);
    if (!out && !known && !axis.placeless.some((i) => i.id === view.selected)) return;
    // Answered even when it needs nothing done, so that a resident who then
    // narrows the ring over the open row is not dragged back outwards.
    answered = view.selected;
    if (!out?.position) return;

    const wider = radiusIncluding(haversine(centre, out.position));
    // One `'replace'` write for a radius change, one `'open'` write for a pan —
    // the rule every other radius and centre change obeys (§2.6).
    if (wider == null) moveCentre(out.position);
    else viewState.set({ radius: wider }, 'replace');
  });
}
